import {
  ArrowRight,
  Bell,
  Building2,
  Coins,
  Eye,
  EyeOff,
  Gift,
  HandCoins,
  Headphones,
  LoaderCircle,
  Info,
  KeyRound,
  Pickaxe,
  Ticket,
  TrendingDown,
  TrendingUp,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { formatUsd } from "../utils/money";
import { catalogMinerToProduct, marketplaceApi } from "../api/marketplace";
import { ProductCard } from "../components/ProductCard";
import { useSession } from "../store/session";
import { useCopy } from "../i18n/copy";
import { useMarketQuotes } from "../hooks/useMarketQuotes";
import { configApi } from "../api/config";
import { RuntimeCounter } from "../components/RuntimeCounter";
import {
  HomeAnnouncementDialog,
  HomeCarousel,
  HomeNoticeTicker,
  useHomeCommunications,
} from "../components/HomeCommunications";

const actions = [
  {
    label: "Depositar",
    to: "/wallet/deposit",
    icon: WalletCards,
    tone: "sun",
  },
  {
    label: "Retirar",
    to: "/wallet/withdraw",
    icon: HandCoins,
    tone: "blue",
  },
  {
    label: "Invitar",
    to: "/growth/invite",
    icon: UsersRound,
    tone: "green",
  },
  {
    label: "Premios",
    to: "/rewards/raffle",
    icon: Gift,
    tone: "earth",
  },
];

const services = [
  { label: "Mis equipos", to: "/miners", icon: Pickaxe, tone: "blue" },
  { label: "Canjear CDK", to: "/rewards/cdk", icon: KeyRound, tone: "earth" },
  { label: "Centro de puntos", to: "/rewards/points", icon: Coins, tone: "green" },
  { label: "Mis cupones", to: "/rewards/coupons", icon: Ticket, tone: "sun" },
  { label: "Sobre GOP ALA", to: "/about", icon: Info, tone: "blue" },
  {
    label: "Perfil de la empresa",
    to: "/company",
    icon: Building2,
    tone: "green",
  },
];

export function HomePage() {
  const { user, isPreview } = useSession();
  const [visible, setVisible] = useState(true);
  const { tr } = useCopy();
  const { quotes, status: marketStatus } = useMarketQuotes();
  const communications = useHomeCommunications();
  const runtime = useQuery({
    queryKey: ["runtime"],
    queryFn: configApi.runtime,
    staleTime: 60000,
  });
  const catalog = useQuery({
    queryKey: ["home-catalog"],
    queryFn: () => marketplaceApi.catalog(1),
    enabled: !isPreview,
  });
  const recommended = (catalog.data || [])
    .filter((item) => item.is_sold_out !== 2)
    .slice(0, 3)
    .map((item) => catalogMinerToProduct(item, 1));

  return (
    <div className="page home-page">
      <section className="hero-shell">
        <div className="hero-backdrop" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <div className="hero-topline">
          <div>
            <p className="eyebrow light">{tr("Panel personal")}</p>
            <h1>
              {tr("Hola")}, {user.username.split(" ")[0]}
            </h1>
          </div>
          <Link
            className="hero-icon page-message-link"
            to="/messages"
            aria-label={tr("Ver notificaciones")}
          >
            <Bell size={21} />
            <span />
          </Link>
        </div>
        <div className="hero-grid">
          <div className="balance-card">
            <div className="balance-label">
              <span>{tr("Saldo disponible")}</span>
              <button
                onClick={() => setVisible(!visible)}
                aria-label={tr(visible ? "Ocultar saldo" : "Mostrar saldo")}
              >
                {visible ? <EyeOff /> : <Eye />}
              </button>
            </div>
            <strong>{visible ? formatUsd(user.balance) : "$ ••••••"}</strong>
          </div>
        </div>
      </section>

      <div className="home-communications">
        <HomeCarousel items={communications.carousel} />
        <HomeNoticeTicker content={communications.ticker} />
      </div>

      <section className="quick-grid" aria-label={tr("Acciones rápidas")}>
        {actions.map(({ label, to, icon: Icon, tone }) => (
          <Link className="quick-action" to={to} key={to}>
            <span className={`quick-icon tone-${tone}`}>
              <Icon />
            </span>
            <span>
              <strong>{tr(label)}</strong>
            </span>
            <ArrowRight size={18} />
          </Link>
        ))}
      </section>

      <section className="home-services" aria-label={tr("Servicios")}>
        {services.map(({ label, to, icon: Icon, tone }) => (
          <Link to={to} className="home-service-link" key={to}>
            <span className={"quick-icon tone-" + tone}>
              <Icon />
            </span>
            <strong>{tr(label)}</strong>
            <ArrowRight />
          </Link>
        ))}
      </section>

      {runtime.data && <RuntimeCounter value={runtime.data} />}

      <section className="section-block home-market">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{tr("Referencia de mercado")}</p>
            <h2>{tr("Activos destacados")}</h2>
          </div>
        </div>
        <div className="market-panel">
          <div className="market-panel-head">
            <span className={"live-label " + marketStatus}>
              <i />
              {tr(
                marketStatus === "live"
                  ? "Datos en vivo"
                  : marketStatus === "unavailable"
                    ? "Mercado temporalmente no disponible"
                    : "Conectando al mercado…",
              )}
            </span>
            <small>{tr("Cambio 24 h")}</small>
          </div>
          {!quotes.length && marketStatus !== "unavailable" && (
            <div className="market-loading">
              <LoaderCircle className="spin" />
            </div>
          )}
          {marketStatus === "unavailable" && !quotes.length && (
            <div className="market-empty">
              {tr("Mercado temporalmente no disponible")}
            </div>
          )}
          <div className="quote-list">
            {quotes.map((quote) => (
              <article className="quote-row" key={quote.symbol}>
                <span
                  className={quote.isPlatform ? "coin platform-coin" : "coin"}
                >
                  <img
                    src={quote.icon}
                    alt=""
                    width="34"
                    height="34"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span>
                  <strong>{quote.symbol}</strong>
                  <small>{tr("Mercado spot")}</small>
                </span>
                <strong>
                  $
                  {new Intl.NumberFormat(
                    document.documentElement.lang || "es-EC",
                    {
                      minimumFractionDigits: quote.precision,
                      maximumFractionDigits: quote.precision,
                    },
                  ).format(quote.price)}
                </strong>
                <span
                  className={
                    "change " +
                    (quote.isPlatform
                      ? "neutral"
                      : quote.change >= 0
                        ? "positive"
                        : "negative")
                  }
                >
                  {!quote.isPlatform &&
                    (quote.change >= 0 ? <TrendingUp /> : <TrendingDown />)}
                  {!quote.isPlatform && quote.change >= 0 ? "+" : ""}
                  {quote.change.toFixed(2)}%
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{tr("Selección recomendada")}</p>
            <h2>{tr("Equipos con propósito")}</h2>
          </div>
          <Link to="/market">
            {tr("Explorar mercado")} <ArrowRight />
          </Link>
        </div>
        {catalog.isLoading && (
          <div className="loading-row">{tr("Cargando equipos…")}</div>
        )}
        <div className="product-grid">
          {recommended.map((product) => (
            <ProductCard product={product} key={product.id} />
          ))}
        </div>
        {isPreview && (
          <div className="empty-state compact">
            <Pickaxe />
            <h3>
              {tr("Inicia sesión para consultar los equipos disponibles")}
            </h3>
            <Link className="button primary" to="/auth/login">
              {tr("Iniciar sesión")} <ArrowRight />
            </Link>
          </div>
        )}
        {!isPreview && !catalog.isLoading && !recommended.length && (
          <div className="empty-state compact">
            <Pickaxe />
            <h3>{tr("No hay equipos disponibles")}</h3>
          </div>
        )}
      </section>

      <section className="support-banner">
        <span>
          <Headphones />
        </span>
        <div>
          <p className="eyebrow light">{tr("Soporte cercano")}</p>
          <h2>{tr("¿Necesitas ayuda?")}</h2>
        </div>
        <Link className="button light" to="/support">
          {tr("Hablar con soporte")}
        </Link>
      </section>

      <HomeAnnouncementDialog content={communications.announcement} />
    </div>
  );
}
